import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { LOCALES, LocalizedLocaleConfig } from "@core/configs/translation/locales.config";
import { LocaleConfig, TranslationService, TranslationStoreService } from "@ogs-gmbh/ngx-translate";
import { isTranslationInfo } from "@shared/types/translation-info";
import { BehaviorSubject, catchError, EMPTY, firstValueFrom, Observable, switchMap, tap, withLatestFrom } from 'rxjs';
import { CustomerLanguageUpdateDto, parseToCustomerLanguageUpdateDto } from "./types/customer-language-update.type";
import { KeycloakService, SKIP_KEYCLOAK_INTERCEPTOR } from "@ogs/ngx-keycloak";

const CUSTOMER_LANGUAGE_ENDPOINT: string = "api/customers/*/employees/language";

// TODO Deprecate
@Injectable({ providedIn: "root" })
export class LanguageSelectionService {
  private readonly _httpClient: HttpClient = inject(HttpClient);

  private readonly _keycloakService: KeycloakService = inject(KeycloakService);

  /** Temporary Id of the JWT-Subject to notify the Backend for when a specific user changed their language in the registration process */
  private _tempSubjectId: string | undefined = undefined;

  private _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  private _translationService: TranslationService = inject(TranslationService);

  public readonly locales: LocalizedLocaleConfig[] = LOCALES;

  private _currentLocale: WritableSignal<LocaleConfig> = signal(this._translationStoreService.getCurrentLocale());

  private _currentLocale$: BehaviorSubject<LocaleConfig> = new BehaviorSubject<LocaleConfig>(this._currentLocale());

  public currentLocale$: Observable<LocaleConfig> = this._currentLocale$.asObservable();

  constructor () {
    this._translationStoreService.getLocale$()
      .pipe(
        tap((newLocaleConfig: LocaleConfig) => {
          this._currentLocale.set(newLocaleConfig);
          this._currentLocale$.next(newLocaleConfig);
        })
      )
      .subscribe();
    this._translationStoreService.getLocale$()
      .pipe(
        withLatestFrom(this._keycloakService.isAuthorized$()),
        switchMap(([ newLocaleConfig, isAuthorized ]: [LocaleConfig, boolean]) => {
          if (!isAuthorized && !this._tempSubjectId) return EMPTY;

          const languageUpdate: CustomerLanguageUpdateDto = parseToCustomerLanguageUpdateDto(newLocaleConfig);
          // eslint-disable-next-line @tseslint/typedef
          const options = this._tempSubjectId
            ? { params: new HttpParams().set('keycloakId', this._tempSubjectId), headers: new HttpHeaders().set(SKIP_KEYCLOAK_INTERCEPTOR, "true") }
            : undefined;

          return this._httpClient.put<never>(CUSTOMER_LANGUAGE_ENDPOINT, languageUpdate, options).pipe(
            catchError(() => EMPTY)
          );
        })
      )
      .subscribe();
  }


  /**
   * Set the id of a JWT-Subject to be used when the language is changed to change a employees language as well when not authorized yet
   * @param subjectId The id of the JWT-Subject
   */
  public setTemporarySubjectId (subjectId: string | undefined): void {
    this._tempSubjectId = subjectId;
  }

  public setLocale (newLocale: LocaleConfig): void {
    const localeRef: LocaleConfig | undefined = this.locales.find((locale: LocaleConfig): boolean => this.compareLocales(locale, newLocale));

    if (localeRef)
      this._translationStoreService.setLocale(localeRef);
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  public compareLocales (objA: LocaleConfig, objB: LocaleConfig): boolean {
    return objA.value === objB.value;
  }


  // TODO Package-candidate
  // eslint-disable-next-line @tseslint/no-explicit-any
  public async getTranslatedTemplateLiteral (strings: TemplateStringsArray, ...values: any[]): Promise<string> {
    let str: string = "";

    for (let i: number = 0, j: number = strings.length; i < j; i++) {
      str += strings[ i ];

      // eslint-disable-next-line @tseslint/no-unsafe-assignment, @tseslint/no-explicit-any
      const value: any = values[ i ];

      if (isTranslationInfo(value)) {
        try {
          const translatedString: string = await firstValueFrom(
            this._translationService.translateTokenByCurrentLocale$(
              value.token,
              value.defaultText,
              value.scope
            )
          );

          str += translatedString;
        } catch {}
      } else if (value !== undefined) str += value;
    }

    return str;
  }
}
